import React, { useState, createContext, useEffect } from "react";
import { storage } from "../helpers/storage";

export const UserContext = createContext({
  auth: {
    token: null,
    name: null,
    userId: null,
    type: null,
    status: null,
    email: null,
    loaded: null,
    login: () => {},
    logout: () => {},
  },
});

export const UserProvider = ({ children }) => {
  const login = (authData, rememberMe) => {
    setAuth({
      ...auth,
      ...authData,
      loaded: true,
    });

    if (rememberMe) {
      storage.setLocal("auth", { ...authData });
    } else {
      storage.setSession("auth", { ...authData });
    }
  };

  const logout = () => {
    setAuth({
      ...auth,
      token: null,
      name: null,
      userId: null,
      type: null,
      status: null,
      email: null,
      loaded: true,
    });

    // Clear only project-specific storage
    storage.remove("auth");
  };

  const [auth, setAuth] = useState({
    token: null,
    name: null,
    userId: null,
    type: null,
    status: null,
    email: null,
    loaded: false,
    login,
    logout,
  });

  useEffect(() => {
    const existingItem = storage.get("auth");
    const minIssuedAt = Date.parse("2026-09-02T00:00:00+08:00") / 1000;

    const getTokenIssuedAt = (token) => {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.iat;
      } catch (err) {
        return null;
      }
    };

    if (existingItem) {
      const issuedAt = getTokenIssuedAt(existingItem.token);
      const now = Date.now() / 1000;
      if (now >= minIssuedAt && (!issuedAt || issuedAt < minIssuedAt)) {
        storage.remove("auth");
        setAuth((auth) => ({
          ...auth,
          token: null,
          name: null,
          userId: null,
          type: null,
          status: null,
          email: null,
          loaded: true,
        }));
        return;
      }

      setAuth((auth) => ({
        ...auth,
        ...existingItem,
        loaded: true,
      }));
    } else {
      setAuth((auth) => ({
        ...auth,
        loaded: true,
      }));
    }
  }, []);

  return (
    <UserContext.Provider value={{ auth }}>{children}</UserContext.Provider>
  );
};

export const withUser = (Component) => (props) => (
  <UserConsumer>
    {(value) => <Component user={value} {...props} />}
  </UserConsumer>
);

export const UserConsumer = UserContext.Consumer;
